import { FormControlLabel, FormControlLabelProps, Radio, useTheme } from "@mui/material";

import { FormTypography } from "../FormTypography";

import { InvalidThemeErrorMessage } from "@/forms-design-system/InvalidThemeErrorMessage";
import { mergeSx } from "@/helpers/mergeSx";

type FormRadioSelectProps = Omit<FormControlLabelProps, "control"> & {
  size?: "small" | "medium";
  error?: string;
};

export const FormRadioSelect = ({ sx, size = "small", error, ...rest }: FormRadioSelectProps) => {
  const theme = useTheme();

  if (!theme.palette.forms || !theme.palette.formsComponents?.radioSelect) {
    return <InvalidThemeErrorMessage />;
  }

  const isSmall = size === "small";
  const radioSize = isSmall ? 14 : 18; // 16x16 for Small, 20x20 for Medium (with the border)
  const radioInnerCircleSize = isSmall ? 8 : 10;
  const fontSize = isSmall ? 14 : 16;
  const lineHeight = isSmall ? 20 : 24;
  const fontWeight = isSmall ? 400 : 600; // Regular vs SemiBold

  const styles = mergeSx(
    {
      display: "inline-flex",
      margin: 0, // Override default MUI style
      userSelect: "none",
      alignItems: "center",
      gap: theme.spacing(1), // 8px gap between radio and label
      position: "relative", // Ensure absolute positioning works correctly
      ["& .MuiRadio-root"]: {
        // Hide the default radio button but keep it clickable
        position: "absolute",
        opacity: 0,
        width: radioSize,
        height: radioSize,
        margin: 0,
        padding: 0,
        zIndex: 1,
      },
      // Custom radio button styling with CSS
      ["&::before"]: {
        content: '""',
        display: "inline-block",
        width: radioSize,
        height: radioSize,
        borderRadius: "50%",
        border: `1px solid ${theme.palette.formsComponents.radioSelect.radioColor}`,
        backgroundColor: theme.palette.formsComponents.radioSelect.radioBackgroundColor,
        flexShrink: 0,
        position: "relative",
        zIndex: 0,
        transition: "border-color 300ms ease-in, box-shadow 300ms ease-in",
        boxSizing: "border-box",
      },
      ["&:has(.Mui-checked)::before"]: {
        borderColor: theme.palette.formsComponents.radioSelect.radioCheckedColor,
      },
      ["&:has(.Mui-checked)::after"]: {
        content: '""',
        position: "absolute",
        top: "50%",
        left: radioSize / 2,
        transform: "translate(-50%, -50%)",
        width: radioInnerCircleSize,
        height: radioInnerCircleSize,
        borderRadius: "50%",
        backgroundColor: theme.palette.formsComponents.radioSelect.innerCircleColor,
        flexShrink: 0,
        zIndex: 1,
      },
      ["&:has(.Mui-disabled)"]: {
        opacity: 0.4,
      },
      ["& .MuiFormControlLabel-label"]: {
        // Style the label text
        position: "relative",
        zIndex: 1,
        fontSize: `${fontSize}px`,
        lineHeight: `${lineHeight}px`,
        fontWeight,
        fontFamily: "Roboto, sans-serif",
        color: theme.palette.formsComponents.radioSelect.textColor,
        margin: 0,
        padding: 0,
        transition: "color 300ms ease-in",
        ["&.Mui-disabled"]: {
          color: theme.palette.formsComponents.radioSelect.textColor, // Keep same color when disabled
        },
      },
      ["&:hover:not(.Mui-disabled)::before"]: {
        borderColor: theme.palette.formsComponents.radioSelect.radioHoverColor,
      },
      ["&:hover:not(.Mui-disabled) .MuiFormControlLabel-label"]: {
        color: theme.palette.formsComponents.radioSelect.textHoverColor,
      },
      // Focus state
      ["&:has(.Mui-focusVisible)::before"]: {
        boxShadow: `0 0 0 3px ${theme.palette.formsComponents.radioSelect.radioFocusColor}`, // Focus ring with 40 opacity
      },
      // Error state - direct styles without spread
      ...(error && {
        ["&.error::before"]: {
          borderColor: theme.palette.formsComponents.radioSelect.radioErrorColor,
        },
        ["&.error:has(.Mui-checked)::before"]: {
          borderColor: theme.palette.formsComponents.radioSelect.radioErrorColor,
        },
        ["&.error:has(.Mui-checked)::after"]: {
          backgroundColor: theme.palette.formsComponents.radioSelect.radioErrorColor,
        },
      }),
    },
    sx
  );

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: theme.spacing(0.5) }}>
      <FormControlLabel {...rest} control={<Radio disableRipple />} sx={styles} className={error ? "error" : ""} />
      {error && (
        <FormTypography
          vibe="error"
          variant="caption"
          style={{ fontSize: "12px", lineHeight: "16px", fontWeight: 400 }}
        >
          {error}
        </FormTypography>
      )}
    </div>
  );
};
